/**
 * Utility functions for the Scan Quality Report components.
 */

import {formatDate, formatCurrency, formatAccountType} from '../utils/formatters';
export { formatDate, formatCurrency };

/**
 * The account display name — EXACT JS mirror of the backend source of truth
 * (livisibly_common/account_display.py::format_account_display_name). Priority:
 *   1. `_nickname` override (display only).
 *   2. Card / no-account-number fallback: "<card_program> <holder> (<email>)".
 *   3. Standard: "*<last4>[-<sub>] <institution> <type>", where type prefers the
 *      schema-derived `account_type_label` else humanizes the raw code.
 *
 * The two implementations are locked together by a shared-fixture parity test
 * (see __fixtures__/accountDisplayName.fixtures.json, asserted by both this
 * suite and backend test_account_display). To change the format, edit this
 * function, the Python function, and the shared fixtures in one commit.
 *
 * @param {Object} account - Account object (scan JSON or serialized account)
 * @returns {string} Formatted display name
 */
export const formatAccountDisplayName = (account) => {
  if (!account) return 'Unknown Account';

  const clean = (v) => (v === undefined || v === null ? '' : String(v).trim());

  const nickname = clean(account._nickname);
  if (nickname) return nickname;

  const accountNumber = clean(account.account_number);
  const last4 = clean(account.account_number_last4)
    || clean(account.account_number_last_4)
    || (accountNumber ? accountNumber.slice(-4) : '');

  if (!last4 && !accountNumber) {
    // No identifying number at all — card / no-account-number fallback.
    const program = clean(account.card_program) || 'Card';
    const holder = clean(account.account_holder);
    const email = clean(account.account_holder_email);
    let name = program;
    if (holder) name = `${name} ${holder}`;
    if (email) name = `${name} (${email})`;
    return name;
  }

  const sub = clean(account.sub_account_number);
  const institution = clean(account.bank_name) || clean(account.institution_name);
  const type = clean(account.account_type_label) || formatAccountType(account.account_type);

  let name = `*${last4}`;
  if (sub) name = `${name}-${sub}`;
  if (institution) name = `${name} ${institution}`;
  if (type) name = `${name} ${type}`;
  return name;
};

/**
 * Maps a document/scan status to a MUI color and human-readable label.
 *
 * @param {string} status - Raw status string from the JSON
 * @returns {{ color: string, label: string }} MUI color name and display label
 */
export const getStatusColor = (status) => {
  const normalized = (status || '').toUpperCase().replace(/[_\s]+/g, '_');

  const statusMap = {
    DEDUP_AND_PUBLISH_COMPLETE: { color: 'success', label: 'Complete' },
    DATA_QUALITY_REVIEW_PASSED: { color: 'success', label: 'Quality Review Passed' },
    COMPLETE: { color: 'success', label: 'Complete' },
    ACCEPTED: { color: 'success', label: 'Accepted' },
    DUPLICATE: { color: 'info', label: 'Duplicate' },
    CONTENT_MATCH: { color: 'info', label: 'Content Match' },
    HASH_MATCH: { color: 'info', label: 'Hash Match' },
    REVIEW_NEEDED: { color: 'warning', label: 'Review Needed' },
    NEEDS_REVIEW: { color: 'warning', label: 'Needs Review' },
    UNSUPPORTED_DOCUMENT_TYPE: { color: 'warning', label: 'Unsupported Document Type' },
    REJECTED: { color: 'error', label: 'Rejected' },
    FAILED: { color: 'error', label: 'Failed' },
    PROCESSING: { color: 'default', label: 'Processing' },
    PENDING: { color: 'default', label: 'Pending' },
  };

  return statusMap[normalized] || { color: 'default', label: status || 'Unknown' };
};

/**
 * Converts a snake_case quality check key into a human-readable label.
 * e.g. "all_accounts_have_bank_name" → "All accounts have bank name"
 *
 * @param {string} checkKey - Snake_case check identifier
 * @returns {string} Human-readable label
 */
export const formatCheckName = (checkKey) => {
  if (!checkKey) return '';
  return checkKey
    .replace(/_/g, ' ')
    .replace(/\b\w/, (c) => c.toUpperCase());
};

// /**
//  * Formats a date string for display. Accepts ISO date strings or date-only strings.
//  *
//  * @param {string} dateString - Date string (e.g. "2025-02-12" or "2025-02-12T00:00:00Z")
//  * @param {Object} [options] - Intl.DateTimeFormat options
//  * @returns {string} Formatted date string
//  */
// export const formatDate = (dateString, options = {}) => {
//   if (!dateString) return '—';

//   try {
//     // Handle date-only strings (YYYY-MM-DD) to avoid timezone offset issues
//     const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
//     const date = isDateOnly
//       ? new Date(dateString + 'T00:00:00')
//       : new Date(dateString);

//     if (isNaN(date.getTime())) return dateString;

//     const defaultOptions = {
//       year: 'numeric',
//       month: 'short',
//       day: 'numeric',
//       ...options,
//     };

//     return date.toLocaleDateString('en-US', defaultOptions);
//   } catch {
//     return dateString;
//   }
// };

/**
 * Formats a datetime string for display including time.
 *
 * @param {string} dateString - ISO datetime string
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '—';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

// /**
//  * Formats a numeric string or number as US currency.
//  *
//  * @param {string|number} amount - Dollar amount
//  * @returns {string} Formatted currency string (e.g. "$21,225.81")
//  */
// export const formatCurrency = (amount) => {
//   if (amount === null || amount === undefined || amount === '') return '—';

//   const num = typeof amount === 'string' ? parseFloat(amount) : amount;
//   if (isNaN(num)) return String(amount);

//   return new Intl.NumberFormat('en-US', {
//     style: 'currency',
//     currency: 'USD',
//     minimumFractionDigits: 2,
//     maximumFractionDigits: 2,
//   }).format(num);
// };

/**
 * Determines the overall health of quality checks as a color.
 *
 * @param {Object} summary - Quality summary object with passed_checks and total_checks
 * @returns {string} MUI color name: 'success', 'warning', or 'error'
 */
export const getQualityColor = (summary) => {
  if (!summary || !summary.total_checks) return 'default';

  const rate = summary.pass_rate ?? (summary.passed_checks / summary.total_checks) * 100;

  if (rate === 100) return 'success';
  if (rate >= 80) return 'warning';
  return 'error';
};

/**
 * Maps a document_type key to a human-readable label.
 *
 * @param {string} docType - Document type identifier (e.g. "bank_statement")
 * @returns {string} Human-readable label
 */
export const formatDocumentType = (docType) => {
  const typeMap = {
    bank_statement: 'Bank Statement',
    brokerage_statement: 'Brokerage Statement',
    tax_return: 'Tax Return',
    id_card: 'ID Card',
    w2: 'W-2',
  };

  return typeMap[docType] || formatCheckName(docType);
};

/**
 * Extracts the duplicate reason into a user-friendly explanation.
 *
 * @param {string} reason - Duplicate reason code (CONTENT_MATCH, HASH_MATCH, etc.)
 * @returns {string} Human-readable explanation
 */
export const getDuplicateExplanation = (reason) => {
  const explanations = {
    CONTENT_MATCH:
      'This statement was already found in the database with matching account and period details.',
    HASH_MATCH:
      'This PDF file has already been uploaded previously (identical file detected).',
  };

  return explanations[(reason || '').toUpperCase()] || 'Duplicate detected.';
};